import React, { useEffect, useMemo, useState } from 'react';

import {
  Flex, Stack, Link, Text, Image,
} from '@chakra-ui/react';
import { isEmpty } from 'lodash';
import { useQuery } from 'react-query';
import { useHistory, useParams } from 'react-router-dom';

import { Carousel, Icon } from 'components';
import { filters, virtualizedListRelations } from 'constants/show';
import { getters } from 'constants/table';
import ZoomImage from 'containers/Modals/ZoomImage';
import Page from 'containers/Page';
import Info from 'containers/Show/Info';
import Meta from 'containers/Show/Meta';
import RelationsCards from 'containers/Show/RelationsCards';
import TrainingItems from 'containers/Show/TrainingItems';
import VirtualizedList from 'containers/Show/VirtualizedList';
import { ContentRoutes } from 'enums/routes';
import { AccessTypes, MethodTypes } from 'enums/types';
import { policy } from 'polices';
import { handleObjectString } from 'utils/parsers';

const notEditableContents = [ContentRoutes.QRCode];

export default function Show() {
  const { content, id } = useParams<GeneralRoute>();
  const [data, setData] = useState<any>({});
  const [image, setImage] = useState<string>();
  const [shouldRefetch, setShouldRefetch] = useState<boolean>(false);
  const policies = policy(content);
  const { push } = useHistory();
  const { refetch, isFetching } = useQuery(['show', content, id], () => getters[content]({ id }), {
    onSuccess: (res) => setData(res.data),
  });

  useEffect(() => {
    if (policies(MethodTypes.View) === AccessTypes.Disallow) {
      push('/403');
    }
  }, [content]);

  const label = useMemo(() => {
    if (content === ContentRoutes.VehicleAccessories) {
      return data?.serialNumber?.concat(` | ${data?.accessoryType?.name}`);
    }
    return null;
  }, [data]);

  if (isEmpty(data)) {
    return null;
  }

  return (
    <Page
      isCentered
      isLoading={isFetching}>
      <Meta
        data={data}
        label={label}
        refetch={refetch}
        isEditable={!notEditableContents.includes(content as any)}
      />
      <Info data={data} />
      {(content === ContentRoutes.Partner && data.accountableSignatureUrl) && (
        <Flex
          flexDirection="column"
          alignItems="center"
          maxW={200}
          mt={2}
          backgroundColor="white.default"
          borderRadius={8}
          p={3}>
          <ZoomImage
            isOpen={!!image}
            onClose={() => setImage(undefined)}
            data={image}
          />
          <Image
            onClick={() => setImage(data.accountableSignatureUrl)}
            width={200}
            src={data.accountableSignatureUrl}
          />
          <Flex
            mt={4}
            justifyContent="center"
            flexDir="column">
            <Text variant="smallRegular">{data.accountable}</Text>
          </Flex>
        </Flex>
      )}
      {filters[content] && (
        <RelationsCards
          filterBy={filters[content]?.filterBy}
          filterValue={handleObjectString(data, filters[content]?.filterValue)}
          my={6}
        />
      )}
      {content === ContentRoutes.Trainings && (
        <TrainingItems
          my={6}
          contents={data.contents}
        />
      )}
      {content === ContentRoutes.QRCode && (
      <Flex
        as={Link}
        href={data.url}
        flexDir="column"
        cursor="pointer"
        bg="white.default"
        width={200}
        borderRadius={6}
        alignItems="center"
        justifyContent="center"
        p={6}>
        <Icon
          size="3xl"
          name="download"
        />
        <Text
          mt={4}
          variant="xLargeSemiBold">Baixar Códigos
        </Text>
      </Flex>
      )}
      {content === ContentRoutes.Vehicles && (
        <Stack
          hidden={isEmpty(data.vehicleUrls)}
          mb={5}>
          <Text variant="xLargeBold">
            Imagens do veículo
          </Text>
          <Carousel
            maxW="100%"
            images={data.vehicleUrls ?? []}
          />
        </Stack>
      )}
      <Stack spacing={4}>
        {virtualizedListRelations[content]?.map((relation) => (
          <VirtualizedList
            {...relation}
            shouldRefetch={shouldRefetch}
            setShouldRefetch={setShouldRefetch}
            filterValue={handleObjectString(data, relation.filterValue)}
            key={`virtualized-${relation.key}-list`}
          />
        ))}
      </Stack>
    </Page>
  );
}
